import { IncreasePatternOptions, TimeUnitOption } from "../../../../models/simulator/options";
import { IPatientPopulationInput, ITreatmentDurationInput } from "../inputs";
import { v4 as uuidv4 } from "uuid";
import { getDurationInMonths } from "../utils";
import { addMonths } from "date-fns";
import { sumBy } from "@lyfegen-platform/utils";
import { IndicationPatientData } from "./patient-service-input.model";
import {
	ICalculatePatientSplitArgs,
	IGetDiscontinuedPatientsArgs,
	IGetPatientSplitArgs,
	IMonthlyPatientSplit,
	IPatientInfo,
} from "./interfaces";
import { getDistributedPacks } from "../treatment-service/treatment.service";
import moment from "moment";
import { toFixedNumber } from "../../details/output/functions";

export const getPatientSplit = (args: IGetPatientSplitArgs & { groupIndexShare?: number }): IMonthlyPatientSplit => {
	const { currentMonth, currentDate, previousMonthSplit, sectionId, context, groupIndexShare } = args;
	const simulationStarted = context.getSimulationStartDate();
	const simulationEnded = context.getSimulationEndDate();

	const indicationsPatientData: Array<IndicationPatientData> = context.getPatientServiceInput(sectionId).sort((a, b) => {
		return a.index - b.index;
	});

	let currentMonthSplit: Array<IPatientInfo> = [];

	indicationsPatientData.forEach((indicationPatientData) => {
		const { indicationId, patientEnrollment, treatmentDuration } = indicationPatientData;
		const maxDuration = moment(new Date(simulationEnded)).diff(moment(new Date(simulationStarted)), treatmentDuration.unit as any) + 1;
		const verifiedTreatmentDuration = {
			...treatmentDuration,
			duration: treatmentDuration.duration > maxDuration ? maxDuration : treatmentDuration.duration,
		};
		const availableDaysForTreatment = getAvailableDaysForTreatment(verifiedTreatmentDuration);

		previousMonthSplit.activePatients
			.filter(
				(activePatient) =>
					activePatient &&
					activePatient.numberOfActivePatients !== 0 &&
					activePatient.indicationId === indicationPatientData.indicationId
			)
			.forEach((activePatient) => {
				const { numberOfActivePatients, numberOfDiscontinuedPatients } = calculatePatientSplit({
					currentMonth,
					initiallyEnrolledPatients: activePatient.enrolledPatients,
					patientsReceivingTreatment: activePatient.numberOfActivePatients,
					treatmentDuration: verifiedTreatmentDuration,
					treatmentStartMonth: activePatient.treatmentStartMonth,
					simulationStarted,
					evaluationThresholds: context.getEvaluationThresholds(sectionId),
				});

				const { distributedPacks, lastAdministration, reusablePackTracker } = getDistributedPacks({
					currentDate,
					lastAdministration: activePatient.lastAdministration,
					numberOfPatients: activePatient.numberOfActivePatients,
					receivingTreatment: currentMonth - activePatient.treatmentStartMonth + 1,
					currentMonth,
					patientSplitId: activePatient.id,
					sectionId,
					indicationId,
					treatmentStartMonth: activePatient.treatmentStartMonth,
					treatmentDuration: verifiedTreatmentDuration,
					context,
					simulationStarted,
					availableDaysForTreatment,
					previousMonthPackTracker: activePatient.reusablePackTracker,
				});

				currentMonthSplit.push({
					...activePatient,
					numberOfActivePatients,
					numberOfPatients: activePatient.numberOfActivePatients,
					numberOfDiscontinuedPatients,
					distributedPacks,
					lastAdministration: lastAdministration ?? activePatient.lastAdministration,
					isFirstAdministration: false,
					reusablePackTracker,
					sectionId,
				});
			});

		const enrolledPatients = getEnrolledPatients(patientEnrollment, currentMonth, groupIndexShare);
		const enrolledPatientsInPreviousMonths =
			currentMonth > 0 ? getAccumulatedEnrolledPatients(patientEnrollment, currentMonth, groupIndexShare) : enrolledPatients;
		const patientSplitId = uuidv4();

		const { numberOfActivePatients, numberOfDiscontinuedPatients } = calculatePatientSplit({
			currentMonth,
			initiallyEnrolledPatients: enrolledPatients,
			patientsReceivingTreatment: enrolledPatients,
			treatmentDuration: verifiedTreatmentDuration,
			treatmentStartMonth: currentMonth,
			simulationStarted,
			evaluationThresholds: context.getEvaluationThresholds(sectionId),
		});

		const { distributedPacks, lastAdministration, reusablePackTracker } = getDistributedPacks({
			currentDate,
			numberOfPatients: numberOfActivePatients + numberOfDiscontinuedPatients,
			receivingTreatment: 1,
			currentMonth,
			treatmentStartMonth: currentMonth,
			treatmentDuration: verifiedTreatmentDuration,
			patientSplitId,
			sectionId,
			indicationId,
			context,
			simulationStarted,
			availableDaysForTreatment,
		});

		currentMonthSplit.push({
			id: patientSplitId,
			indicationId,
			numberOfPatients: numberOfActivePatients + numberOfDiscontinuedPatients,
			enrolledPatients,
			accumulatedEnrolled: enrolledPatientsInPreviousMonths,
			numberOfActivePatients,
			numberOfDiscontinuedPatients,
			treatmentStartMonth: currentMonth,
			distributedPacks,
			isFirstAdministration: true,
			lastAdministration,
			reusablePackTracker,
			sectionId,
		});
	});

	return {
		currentMonth: currentDate,
		activePatients: currentMonthSplit,
	};
};

export const getEnrolledPatients = (patientEnrollment: IPatientPopulationInput, currentMonth: number, groupIndexShare?: number): number => {
	const allPatients =
		patientEnrollment.type === IncreasePatternOptions.LINEAR
			? getPatientAmountPerMonth(patientEnrollment)
			: patientEnrollment.amount[currentMonth];
	return shareAmount(allPatients, groupIndexShare);
};

const shareAmount = (amount: number, share?: number) => (share ? amount * (share / 100) : amount);

export const getAccumulatedEnrolledPatients = (
	patientEnrollment: IPatientPopulationInput,
	currentMonth: number,
	groupIndexShare?: number
): number => {
	const sumOfEnrolledPatients = ({ amount, groupIndexShare }: { amount: number[]; groupIndexShare?: number }) => {
		const previousAmounts = amount.slice(0, currentMonth + 1);

		return previousAmounts.reduce((acc, c) => (acc += groupIndexShare ? shareAmount(c, groupIndexShare) : c), 0);
	};

	return patientEnrollment.type === IncreasePatternOptions.LINEAR
		? shareAmount(getPatientAmountPerMonth(patientEnrollment) * (currentMonth + 1), groupIndexShare)
		: sumOfEnrolledPatients({ amount: patientEnrollment.amount, groupIndexShare });
};

export const calculatePatientSplit = (args: ICalculatePatientSplitArgs) => {
	const {
		currentMonth,
		initiallyEnrolledPatients,
		patientsReceivingTreatment,
		treatmentDuration,
		treatmentStartMonth,
		simulationStarted,
		evaluationThresholds,
	} = args;
	const startMonth = addMonths(new Date(simulationStarted), treatmentStartMonth);
	const duration = getDurationInMonths(treatmentDuration.duration, treatmentDuration.unit, startMonth);
	const isLastTreatment = duration === currentMonth - treatmentStartMonth + 1;
	if (treatmentDuration.type === IncreasePatternOptions.LINEAR) {
		const discontinuedPatients = getDiscontinuedPatients({
			durationInMonths: currentMonth - treatmentStartMonth,
			evaluationThresholds,
			patientsReceivingTreatment,
			startMonth,
		});
		const activePatients = isLastTreatment ? 0 : patientsReceivingTreatment - discontinuedPatients;
		return {
			numberOfActivePatients: toFixedNumber(activePatients),
			numberOfDiscontinuedPatients: activePatients ? toFixedNumber(discontinuedPatients) : toFixedNumber(patientsReceivingTreatment),
		};
	}

	const patientsWhichNotReceivedAnyTreatment =
		currentMonth - treatmentStartMonth === 0 ? ((100 - treatmentDuration.durationPercentages[0]) * initiallyEnrolledPatients) / 100 : 0;
	const numberOfActivePatients = isLastTreatment
		? 0
		: (treatmentDuration.durationPercentages[currentMonth - treatmentStartMonth + 1] * initiallyEnrolledPatients) / 100;
	const numberOfDiscontinuedPatients = patientsReceivingTreatment - numberOfActivePatients - patientsWhichNotReceivedAnyTreatment;

	return {
		numberOfActivePatients: toFixedNumber(numberOfActivePatients),
		numberOfDiscontinuedPatients: toFixedNumber(numberOfDiscontinuedPatients),
	};
};

export const getDiscontinuedPatients = (args: IGetDiscontinuedPatientsArgs) => {
	const { durationInMonths, evaluationThresholds, patientsReceivingTreatment, startMonth } = args;

	const evaluationThresholdsWithMonthDuration = evaluationThresholds
		.slice()
		.sort((a, b) => a.duration - b.duration)
		.map((threshold) => ({
			...threshold,
			duration: getDurationInMonths(threshold.duration, evaluationThresholds[0].unit, startMonth),
		}));

	if (!evaluationThresholdsWithMonthDuration.length) return 0;

	const matchedEvaluationMonthIndex = evaluationThresholdsWithMonthDuration.findIndex(
		(threshold) => threshold.duration - 1 === durationInMonths //durationInMonths starts from 0
	);

	if (matchedEvaluationMonthIndex !== -1) {
		const current = evaluationThresholdsWithMonthDuration[matchedEvaluationMonthIndex];
		const next = evaluationThresholdsWithMonthDuration[matchedEvaluationMonthIndex + 1];

		// check duration duplication
		if (current.duration === next?.duration) return 0;

		const discontinuedPatients = sumBy(
			current.patientShare.filter((patientShare) => patientShare.isDiscontinued),
			(patientShare) => Number(patientShare.share)
		);

		return (discontinuedPatients * patientsReceivingTreatment) / 100;
	}

	return 0;
};

export const getPatientAmountPerMonth = (patientEnrollment: IPatientPopulationInput) => {
	const amount = +patientEnrollment.amount;
	switch (patientEnrollment.unit) {
		case TimeUnitOption.MONTH:
			return amount;
		case TimeUnitOption.WEEK:
			return amount * 4;
		case TimeUnitOption.YEAR:
			return amount / 12;
		default:
			return amount;
	}
};

export const getAvailableDaysForTreatment = (treatmentDuration: ITreatmentDurationInput) => {
	switch (treatmentDuration.unit) {
		case TimeUnitOption.MONTH:
			return undefined;
		case TimeUnitOption.WEEK:
			return treatmentDuration.duration * 7;
		case TimeUnitOption.DAY:
			return treatmentDuration.duration;
		default:
			return undefined;
	}
};
